/**
 * Stats typed per content item. Every key is optional: a TikTok has no
 * reach, a podcast only plays (views). Only filled numbers are stored.
 */
export interface ChPostMetrics {
  views?: number;
  reach?: number;
  impressions?: number;
  likes?: number;
  comments?: number;
  shares?: number;
  saves?: number;
  clicks?: number;
  followers_gained?: number;
  /** Average watch time in seconds (videos). */
  avg_watch_time?: number;
}

export type ChMetricKey = keyof ChPostMetrics;

export interface MetricField {
  key: ChMetricKey;
  label: string;
  /** Durations are averages: they don't add up across items. */
  kind: 'count' | 'duration';
  /** Only asked for video content. */
  videoOnly?: boolean;
}

export const METRIC_FIELDS: MetricField[] = [
  { key: 'views', label: 'Visualizações', kind: 'count' },
  { key: 'reach', label: 'Alcance', kind: 'count' },
  { key: 'impressions', label: 'Impressões', kind: 'count' },
  { key: 'likes', label: 'Gostos', kind: 'count' },
  { key: 'comments', label: 'Comentários', kind: 'count' },
  { key: 'shares', label: 'Partilhas', kind: 'count' },
  { key: 'saves', label: 'Guardados', kind: 'count' },
  { key: 'clicks', label: 'Cliques', kind: 'count' },
  { key: 'followers_gained', label: 'Seguidores ganhos', kind: 'count' },
  { key: 'avg_watch_time', label: 'Tempo médio de visualização', kind: 'duration', videoOnly: true },
];

const INTERACTION_KEYS: ChMetricKey[] = ['likes', 'comments', 'shares', 'saves'];

/** Accepts whatever came from the database and keeps only valid numbers. */
export function normalizeMetrics(raw: unknown): ChPostMetrics {
  if (!raw || typeof raw !== 'object') return {};
  const out: ChPostMetrics = {};
  for (const { key, kind } of METRIC_FIELDS) {
    const rawValue = (raw as Record<string, unknown>)[key];
    const value = Number(rawValue);
    if (rawValue !== null && rawValue !== undefined && rawValue !== '' && Number.isFinite(value) && value >= 0) {
      // Watch time keeps tenths of a second (TikTok shows 8,2 s)
      out[key] = kind === 'duration' ? Math.round(value * 10) / 10 : Math.round(value);
    }
  }
  return out;
}

export function hasMetrics(m: ChPostMetrics | null | undefined): boolean {
  return !!m && Object.keys(normalizeMetrics(m)).length > 0;
}

/** Likes + comments + shares + saves. */
export function getEngagement(m: ChPostMetrics): number {
  return INTERACTION_KEYS.reduce((sum, key) => sum + (m[key] || 0), 0);
}

export function hasInteractions(m: ChPostMetrics): boolean {
  return INTERACTION_KEYS.some(k => m[k] !== undefined);
}

/**
 * Interactions over reach (the platforms' own definition); falls back to
 * views when reach isn't available (TikTok, YouTube). Null when neither.
 */
export function getEngagementRate(m: ChPostMetrics): number | null {
  const base = m.reach || m.views || 0;
  if (base <= 0) return null;
  if (!hasInteractions(m)) return null;
  return getEngagement(m) / base;
}

/**
 * One item's stats: a single set, or one set per platform when it went to
 * several (an Instagram reach and a TikTok view can't share a rate).
 */
export type MetricsItem = ChPostMetrics | ChPostMetrics[] | null | undefined;

export interface MetricsSummary {
  totals: ChPostMetrics;
  engagement: number;
  /** Rate over all items that have both interactions and reach/views. */
  engagementRate: number | null;
  itemsWithStats: number;
}

export function summarizeMetrics(list: MetricsItem[]): MetricsSummary {
  const totals: ChPostMetrics = {};
  let itemsWithStats = 0;
  let rateInteractions = 0;
  let rateBase = 0;
  // Watch time is averaged, weighted by views where there are views
  let watchWeighted = 0;
  let watchWeight = 0;

  for (const item of list) {
    const units = (Array.isArray(item) ? item : [item])
      .map(normalizeMetrics)
      .filter(m => Object.keys(m).length > 0);
    if (units.length === 0) continue;
    itemsWithStats++;
    for (const m of units) {
      for (const { key, kind } of METRIC_FIELDS) {
        if (kind === 'count' && m[key] !== undefined) totals[key] = (totals[key] || 0) + (m[key] as number);
      }
      if (m.avg_watch_time !== undefined) {
        const weight = m.views || 1;
        watchWeighted += m.avg_watch_time * weight;
        watchWeight += weight;
      }
      // Only units that can produce a rate count towards the overall rate, so
      // a post without reach doesn't dilute it
      if (getEngagementRate(m) !== null) {
        rateInteractions += getEngagement(m);
        rateBase += m.reach || m.views || 0;
      }
    }
  }

  if (watchWeight > 0) totals.avg_watch_time = Math.round((watchWeighted / watchWeight) * 10) / 10;

  return {
    totals,
    engagement: getEngagement(totals),
    engagementRate: rateBase > 0 ? rateInteractions / rateBase : null,
    itemsWithStats,
  };
}

/** Per-platform sets folded into the single set stored in `metrics`. */
export function combineMetrics(sets: ChPostMetrics[]): ChPostMetrics {
  return summarizeMetrics(sets.map(s => [s])).totals;
}

/** 1 234 → "1,2 mil", 2 500 000 → "2,5 M" (pt-PT). */
export function formatCompactNumber(n: number): string {
  return new Intl.NumberFormat('pt-PT', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(n);
}

export function formatPercent(rate: number): string {
  return new Intl.NumberFormat('pt-PT', {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(rate);
}

/** 8.2 → "8,2 s", 65 → "1:05". */
export function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${new Intl.NumberFormat('pt-PT', { maximumFractionDigits: 1 }).format(seconds)} s`;
  }
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
}

/** "1:05" / "65" / "8,2" / "8.2s" → seconds; null when it isn't a duration. */
export function parseDuration(input: string): number | null {
  const text = input.trim().replace(/\s*s$/i, '');
  if (!text) return null;
  if (text.includes(':')) {
    const parts = text.split(':').map(p => p.trim());
    if (parts.some(p => !/^\d+([.,]\d+)?$/.test(p))) return null;
    return parts.reduce((acc, p) => acc * 60 + Number(p.replace(',', '.')), 0);
  }
  if (!/^\d+([.,]\d+)?$/.test(text)) return null;
  return Number(text.replace(',', '.'));
}

export function formatMetricValue(key: ChMetricKey, value: number): string {
  return METRIC_FIELDS.find(f => f.key === key)?.kind === 'duration'
    ? formatDuration(value)
    : formatCompactNumber(value);
}

/** Two-hour windows people post in, for the best-time insight. */
const TIME_SLOTS = [
  [0, 7, '00h–07h'], [7, 10, '07h–10h'], [10, 13, '10h–13h'], [13, 16, '13h–16h'],
  [16, 19, '16h–19h'], [19, 22, '19h–22h'], [22, 24, '22h–00h'],
] as const;

export function getTimeSlotLabel(date: Date): string {
  const h = date.getHours();
  return TIME_SLOTS.find(([from, to]) => h >= from && h < to)![2];
}

export interface BestTime {
  label: string;
  rate: number;
  items: number;
}

/**
 * The time slot whose items had the highest engagement rate. Needs at least
 * three timed items with a rate spread over two or more slots to mean anything.
 */
export function getBestPublishingTime(
  items: Array<{ published_at?: string | null; metrics: MetricsItem }>
): BestTime | null {
  const bySlot = new Map<string, { interactions: number; base: number; items: number }>();
  let timed = 0;
  for (const item of items) {
    if (!item.published_at) continue;
    const date = new Date(item.published_at);
    if (Number.isNaN(date.getTime())) continue;
    const units = (Array.isArray(item.metrics) ? item.metrics : [item.metrics]).map(normalizeMetrics);
    let base = 0;
    let interactions = 0;
    for (const m of units) {
      if (getEngagementRate(m) === null) continue;
      base += m.reach || m.views || 0;
      interactions += getEngagement(m);
    }
    if (base <= 0) continue;
    const label = getTimeSlotLabel(date);
    const slot = bySlot.get(label) || { interactions: 0, base: 0, items: 0 };
    slot.interactions += interactions;
    slot.base += base;
    slot.items++;
    bySlot.set(label, slot);
    timed++;
  }
  if (timed < 3 || bySlot.size < 2) return null;
  let best: BestTime | null = null;
  for (const [label, slot] of bySlot) {
    const rate = slot.interactions / slot.base;
    if (!best || rate > best.rate) best = { label, rate, items: slot.items };
  }
  return best;
}
